import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "../../../shared/toast";
import {
  downloadTranscript,
  TranscriptDownloadFormat,
} from "../../../shared/api/transcriptDownloadApi";

interface UseTranscriptDownloadOptions {
  /** Whether the editor has unsaved edits — if so, save before downloading. */
  isDirty: boolean;
  /** Version currently shown in the editor (-1/null = unknown → server head). */
  currentVersion: number | null;
  /**
   * Saves the current draft and resolves with the new head version number, or
   * null if the save failed (the caller surfaces its own error toast).
   */
  save: () => Promise<number | null>;
}

/** Outcome of one download run — `completed: false` means the pre-download
 *  save failed (which already surfaced its own error), not a download error. */
interface DownloadOutcome {
  completed: boolean;
}

/**
 * Transcript-download flow for the editor: a format menu (TXT / DOCX / PDF)
 * opens the language-selection modal, then the file is generated *server-side*
 * (bodycam/evidence service). To download exactly what's on screen, unsaved
 * edits are saved first (appending a new version) and that version is requested;
 * an unedited transcript downloads the version currently being viewed. The
 * native language uses the stored transcript; other languages are translated
 * server-side.
 *
 * The download action runs as a useMutation. Nothing is cached (a download is
 * a one-shot side effect), and the flow stays pessimistic like the old
 * hand-rolled version; the menu/modal remain plain client state.
 */
export const useTranscriptDownload = (
  evidenceId: string | null,
  { isDirty, currentVersion, save }: UseTranscriptDownloadOptions,
) => {
  const toast = useToast();
  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<null | HTMLElement>(null);
  // Format chosen from the menu; non-null opens the language modal.
  const [languageModalFormat, setLanguageModalFormat] =
    useState<TranscriptDownloadFormat | null>(null);
  // Synchronous re-entrancy guard: useMutation's isPending only updates on
  // re-render, so a ref is still needed to reject a second call that lands
  // before the pending state re-renders.
  const downloadingRef = useRef(false);

  // The whole save-then-download flow is one mutation so `isPending` covers
  // the save step too (the old `downloading` flag did). A failed pre-download
  // save resolves `{ completed: false }` instead of rejecting: that path has
  // already surfaced its own error and must leave the modal open WITHOUT the
  // download-failure toast.
  const downloadMutation = useMutation<
    DownloadOutcome,
    unknown,
    {
      evidenceId: string;
      format: TranscriptDownloadFormat;
      languageCode: string;
    }
  >({
    mutationFn: async ({ evidenceId: id, format, languageCode }) => {
      let version = currentVersion;
      if (isDirty) {
        version = await save();
        // Save failed (already surfaced its own error) — leave the modal open.
        if (version == null) return { completed: false };
      }
      await downloadTranscript({
        evidenceId: id,
        format,
        languageCode,
        version: version != null && version >= 0 ? version : undefined,
      });
      return { completed: true };
    },
  });

  /**
   * Runs the download for the selected language + the format chosen from the
   * menu. On failure the modal stays open so the user can retry.
   */
  const handleLanguageDownload = async (languageCode: string) => {
    if (!languageModalFormat || !evidenceId) return;
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    try {
      const { completed } = await downloadMutation.mutateAsync({
        evidenceId,
        format: languageModalFormat,
        languageCode,
      });
      if (completed) setLanguageModalFormat(null);
    } catch (error) {
      console.error("Failed to download transcript:", error);
      toast.error("Failed to download the transcript. Please try again.");
    } finally {
      downloadingRef.current = false;
    }
  };

  return {
    downloadMenuAnchor,
    openDownloadMenu: (el: HTMLElement) => setDownloadMenuAnchor(el),
    closeDownloadMenu: () => setDownloadMenuAnchor(null),
    languageModalFormat,
    selectFormat: (format: TranscriptDownloadFormat) =>
      setLanguageModalFormat(format),
    closeLanguageModal: () => setLanguageModalFormat(null),
    handleLanguageDownload,
    downloading: downloadMutation.isPending,
  };
};
